export type InterpolationFunction = (values: number[], progress: number) => number;

export function linear(p0: number, p1: number, progress: number): number {
  return (p1 - p0) * progress + p0;
}

function factorial(value: number): number {
  if (value === 0) {
    return 1;
  }

  let res: number = value;
  // eslint-disable-next-line no-param-reassign,no-plusplus
  while (--value) {
    res *= value;
  }

  return res;
}

function bernstein(n: number, i: number): number {
  return factorial(n) / factorial(i) / factorial(n - i);
}

export function linearInterpolation(values: number[], progress: number): number {
  const m: number = values.length - 1;
  const f: number = m * progress;
  const i: number = Math.floor(f);

  if (progress < 0) {
    return linear(values[0], values[1], f);
  }

  if (progress > 1) {
    return linear(values[m], values[m - 1], m - f);
  }

  return linear(values[i], values[i + 1 > m ? m : i + 1], f - i);
}

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const v0: number = (p2 - p0) * 0.5;
  const v1: number = (p3 - p1) * 0.5;
  const t2: number = t * t;
  const t3: number = t * t2;
  return (2 * p1 - 2 * p2 + v0 + v1) * t3 + (-3 * p1 + 3 * p2 - 2 * v0 - v1) * t2 + v0 * t + p1;
}

export function bezierInterpolation(values: number[], progress: number): number {
  let b = 0;
  const n: number = values.length - 1;

  for (let i = 0; i <= n; i += 1) {
    b += (1 - progress) ** (n - i) * progress ** i * values[i] * bernstein(n, i);
  }

  return b;
}

export function catmullRomInterpolation(v: number[], progress: number): number {
  const m: number = v.length - 1;
  let f: number = m * progress;
  let i: number = Math.floor(f);

  if (v[0] === v[m]) {
    if (progress < 0) {
      i = Math.floor((f = m * (1 + progress)));
    }

    return catmullRom(v[(i - 1 + m) % m], v[i], v[(i + 1) % m], v[(i + 2) % m], f - i);
  }
  if (progress < 0) {
    return v[0] - (catmullRom(v[0], v[0], v[1], v[1], -f) - v[0]);
  }

  if (progress > 1) {
    return v[m] - (catmullRom(v[m], v[m], v[m - 1], v[m - 1], f - m) - v[m]);
  }

  return catmullRom(v[i ? i - 1 : 0], v[i], v[m < i + 1 ? m : i + 1], v[m < i + 2 ? m : i + 2], f - i);
}

export const interpolationMap: Record<string, InterpolationFunction> = {
  linear: linearInterpolation,
  bezier: bezierInterpolation,
  catmull: catmullRomInterpolation,
};
